import type { CuratorCommentDocument } from '@/types'
import {
  typedCollection,
  typedDoc,
  getCollection,
  addDoc,
  deleteDocument,
  serverTimestamp,
  orderBy,
} from '@/lib/firebase/firestore'
import { createUserFacingError } from '@/lib/errors'

/**
 * CRUD over the curatorComments subcollection of a submission. Documents are
 * sorted ascending by createdAt for natural thread ordering.
 */
function commentsCol(submissionId: string) {
  return typedCollection<CuratorCommentDocument>(
    `applicationSubmissions/${submissionId}/curatorComments`
  )
}

function commentDoc(submissionId: string, commentId: string) {
  return typedDoc<CuratorCommentDocument>(
    `applicationSubmissions/${submissionId}/curatorComments`,
    commentId
  )
}

export const curatorCommentsService = {
  async list(submissionId: string): Promise<Array<CuratorCommentDocument & { id: string }>> {
    return getCollection(commentsCol(submissionId), orderBy('createdAt', 'asc'))
  },

  async add(
    submissionId: string,
    args: { authorUid: string; authorName: string; body: string }
  ): Promise<string> {
    const trimmed = args.body.trim()
    if (!trimmed) throw createUserFacingError('Comment body is required.')
    const payload = {
      submissionId,
      authorUid: args.authorUid,
      authorName: args.authorName,
      body: trimmed,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(commentsCol(submissionId), payload as unknown as CuratorCommentDocument)
    return ref.id
  },

  async delete(submissionId: string, commentId: string): Promise<void> {
    await deleteDocument(commentDoc(submissionId, commentId))
  },
}
